import React, { useCallback, useRef, useState } from 'react';
import { SISI_FREQS } from './specialLogic';

/**
 * SISIGraph — Frequency vs SISI score (%) line chart with RE/LE lines.
 */
const SISIGraph = ({
  right, left,       // { '250': 10, ... }
  plotEar = 'right', // active target for graph clicks
  onSetPlotEar,
  onSetValue,        // (ear, freq, pct) => void
  readOnly = false,
  width = 460, height = 220,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 42, marginR = 12, marginT = 28, marginB = 34;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;

  const xScale = useCallback(
    (i) => marginL + (i * plotW) / Math.max(1, SISI_FREQS.length - 1),
    [plotW],
  );
  const yScale = useCallback((pct) => marginT + ((100 - pct) * plotH) / 100, [plotH]);
  const yInv = useCallback(
    (py) => {
      const raw = 100 - ((py - marginT) * 100) / plotH;
      return Math.max(0, Math.min(100, Math.round(raw)));
    },
    [plotH],
  );

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 8 || p.x > width - marginR + 8) return null;
    let bestIdx = 0, bestDx = Infinity;
    SISI_FREQS.forEach((_, i) => {
      const dx = Math.abs(xScale(i) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    const y = Math.max(marginT, Math.min(height - marginB, p.y));
    return { freq: SISI_FREQS[bestIdx], pct: yInv(y), idx: bestIdx };
  };

  const handleClick = (evt) => {
    if (readOnly || !onSetValue) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    onSetValue(plotEar, s.freq, s.pct);
  };

  const buildPath = (bag, color) => {
    if (!bag) return null;
    let d = '';
    SISI_FREQS.forEach((f, i) => {
      const v = bag[String(f)];
      if (v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v))) {
        d += `${d ? 'L' : 'M'}${xScale(i)},${yScale(Number(v))} `;
      }
    });
    return { d: d.trim(), color, bag };
  };
  const rPath = buildPath(right, '#dc2626');
  const lPath = buildPath(left, '#2563eb');

  const yTicks = [0, 20, 40, 60, 80, 100];

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="sisi-ear-chips">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot ear:</span>
          {['right', 'left'].map((e) => (
            <button
              key={e}
              type="button"
              data-testid={`sisi-plot-ear-${e}`}
              onClick={() => onSetPlotEar && onSetPlotEar(e)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${plotEar === e ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              style={plotEar === e ? { backgroundColor: e === 'right' ? '#dc2626' : '#2563eb' } : {}}
            >{e === 'right' ? 'Right (RE)' : 'Left (LE)'}</button>
          ))}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={(e) => { const s = snap(toViewBox(e)); setHover(s); }}
        onMouseLeave={() => setHover(null)}
        data-testid="sisi-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Legend inside graph */}
        <g>
          <circle cx={width - marginR - 60} cy={marginT - 12} r="3" fill="#dc2626" />
          <text x={width - marginR - 54} y={marginT - 9} fontSize="9" fill="#334155">Right Ear (RE)</text>
        </g>
        {/* Grid + axes */}
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        {SISI_FREQS.map((f, i) => (
          <g key={f}>
            <text x={xScale(i)} y={height - marginB + 12} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">{f >= 1000 ? `${f / 1000}K` : f}</text>
          </g>
        ))}
        <text x={marginL - 32} y={marginT + plotH / 2 - 20} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 32}, ${marginT + plotH / 2})`} textAnchor="middle">SISI Score (%)</text>
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Frequency (Hz)</text>
        {/* Left ear line */}
        {lPath?.d && <path d={lPath.d} fill="none" stroke={lPath.color} strokeWidth="1.8" />}
        {lPath && SISI_FREQS.map((f, i) => {
          const v = lPath.bag?.[String(f)];
          if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
          return <circle key={`l-${f}`} cx={xScale(i)} cy={yScale(Number(v))} r="3" fill={lPath.color} />;
        })}
        {/* Right ear line (drawn on top) */}
        {rPath?.d && <path d={rPath.d} fill="none" stroke={rPath.color} strokeWidth="2" />}
        {rPath && SISI_FREQS.map((f, i) => {
          const v = rPath.bag?.[String(f)];
          if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return null;
          return <circle key={`r-${f}`} cx={xScale(i)} cy={yScale(Number(v))} r="3.5" fill={rPath.color} />;
        })}
        {/* Hover ghost */}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="sisi-hover">
            <line x1={xScale(hover.idx)} x2={xScale(hover.idx)} y1={marginT} y2={height - marginB} stroke={plotEar === 'right' ? '#dc2626' : '#2563eb'} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            <circle cx={xScale(hover.idx)} cy={yScale(hover.pct)} r="4" fill={plotEar === 'right' ? '#dc2626' : '#2563eb'} opacity="0.85" />
            <rect x={xScale(hover.idx) + 6} y={Math.max(marginT, yScale(hover.pct) - 10)} width="46" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={xScale(hover.idx) + 8} y={Math.max(marginT, yScale(hover.pct) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.freq >= 1000 ? `${hover.freq / 1000}K` : hover.freq} · {hover.pct}%
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default SISIGraph;
